import type { ComplexDate } from '@/composables/life/life'
import i18n from '@/utils/i18n'
import { currentLang } from '@/composables/settings/storage'

function parseDateTime(dateTimeString: string): Date {
  // Split the date and time components
  const [datePart, timePart] = dateTimeString.split(' ')

  // Extract year, month, and day from the date part
  const [year, month, day] = datePart.split('-')

  // Extract hours, minutes, seconds, and milliseconds from the time part
  const [hours, minutes, secondsAndMilliseconds] = timePart.split(':')
  const [seconds, milliseconds] = secondsAndMilliseconds.split('.')

  // Create a new Date object
  const parsedDate = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hours),
    parseInt(minutes),
    parseInt(seconds),
    parseInt(milliseconds)
  )

  return parsedDate
}

/**
 *
 * @param date parse YYYY-MM-dd string to Date
 */
export function parseDate(date: string | null): Date | null {
  if (date) {
    const [year, month, day] = date.split('-')
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  } else {
    return null
  }
}

export function formatFriendlyTime(
  timestamp: number,
  lang: string = 'zh_CN',
  log: boolean = false,
  showDetails: boolean = false,
  showTime: boolean = true
): string {
  lang = lang.replace('_', '-')

  const incomingDate = new Date(timestamp)
  const currentDate = new Date()

  const diff = currentDate.getTime() - incomingDate.getTime()
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)

  if (log) {
    console.log(incomingDate, 'weeks:', weeks, 'days:', days, 'hours:', hours, 'minutes:', minutes, 'seconds:', seconds)
  }

  let result = ''
  let fullDate = ''
  if (weeks > 0) {
    // More than a week ago, format as month-day hour:minute or year-month-day hour:minute
    let options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }
    if (currentDate.getFullYear() !== incomingDate.getFullYear()) {
      if (showDetails) {
        options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }
      } else {
        options = { year: 'numeric', month: 'short', day: 'numeric' }
      }
    } else {
      options = showTime
        ? { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }
        : { month: 'short', day: 'numeric' }
    }
    fullDate = incomingDate.toLocaleDateString(lang, options)
  } else if (days > 0) {
    // Less than a week ago but more than a day ago
    if (days == 1) {
      result = i18n.global.t('dp.yesterday')
    } else {
      result = `${days} ${
        days > 1 && lang.indexOf('en') > -1 ? i18n.global.t('dp.days') : i18n.global.t('dp.day')
      } ${i18n.global.t('dp.before')}`
    }
  } else if (hours > 0) {
    result = `${hours} ${
      hours > 1 && lang.indexOf('en') > -1 ? i18n.global.t('dp.hour') : i18n.global.t('dp.hours')
    } ${i18n.global.t('dp.before')}`
    // Less than a day ago but more than an hour ago
  } else if (minutes > 0) {
    // Less than an hour ago but more than a minute ago
    result = `${minutes} ${
      minutes > 1 && lang.indexOf('en') > -1 ? i18n.global.t('dp.minute') : i18n.global.t('dp.minutes')
    } ${i18n.global.t('dp.before')}`
  } else if (seconds >= 0) {
    // Less than a minute ago
    result = `${i18n.global.t('dp.recent')}`
  } else {
    result = `${i18n.global.t('dp.recent')}`
  }
  if (lang.includes('zh') && result !== '') {
    result = result.replace(/\s/g, '')
  }
  if (result == '') {
    result = fullDate
  }
  return result
}

export function displayYearPeriod(
  start: ComplexDate | null | undefined,
  end: ComplexDate | null | undefined,
  isPassAway: boolean = false
): string {
  console.log('start:', start, ' - end:', end)
  if (!isPassAway) {
    return ' '
  }
  let timeOutput: string = ''
  if (start && start.date) {
    const startDate = new Date(start.date)
    if (startDate) {
      timeOutput += '' + startDate.getFullYear()
    }
    if (end && end.date !== null && end.date !== undefined) {
      const endDate = new Date(end.date)
      timeOutput += '-' + endDate.getFullYear()
    } else {
      timeOutput += ' -   '
    }
  } else {
    timeOutput = ' '
  }
  console.log('timeOutput:', timeOutput)
  return timeOutput
}

// 计算一个date到今天是多少天，如果超过1年的就是n年x天
export function calculateDaysFromToday(date: ComplexDate | null | undefined, lang: string) {
  // 给出详细计算过程及多语言考虑
  // 注意：ComplexDate.date 是 number，时间戳 0（1970-01-01）是有效值，不能用 !date.date 判断
  if (!date || date.date === null || date.date === undefined) {
    return ''
  }

  const startDate = new Date(date.date)

  const today = new Date()
  const diffInTime = today.getTime() - startDate.getTime()
  const diffInDays = Math.ceil(diffInTime / (1000 * 3600 * 24))

  let result = ''

  // if (diffInDays < 365) {
  result = `${diffInDays}${
    diffInDays > 1 && lang.indexOf('en') > -1 ? ' ' + i18n.global.t('dp.days') : i18n.global.t('dp.day')
  }`
  // } else {
  //   const years = Math.floor(diffInDays / 365)
  //   const remainingDays = diffInDays % 365
  //   result = `${years}${
  //     years > 1 && lang.indexOf('en') > -1 ? ' ' + i18n.global.t('dp.years') : i18n.global.t('dp.year')
  //   }${
  //     remainingDays > 1 && lang.indexOf('en') > -1
  //       ? ' ' +remainingDays + ' ' + i18n.global.t('dp.days')
  //       : remainingDays + i18n.global.t('dp.day')
  //   }`
  // }

  return result
}

export function formatDate(timestamp: number, lang: string, showThisYear: boolean = false) {
  lang = lang.replace('_', '-')
  const incomingDate = new Date(timestamp)
  const now = new Date()
  const options: Intl.DateTimeFormatOptions =
    incomingDate.getFullYear() == now.getFullYear() && !showThisYear
      ? { month: 'short', day: 'numeric' }
      : { year: 'numeric', month: 'short', day: 'numeric' }
  return incomingDate.toLocaleDateString(lang, options)
}

export function formatDateTime(timestamp: number, lang: string) {
  lang = lang.replace('_', '-')
  const incomingDate = new Date(timestamp)
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }
  return incomingDate.toLocaleDateString(lang, options)
}

export function formatDateAfterBefore(timestamp: number, lang: string): string {
  lang = lang.replace('_', '-')

  const incomingDate = new Date(timestamp)
  const currentDate = new Date()

  const diff = incomingDate.getTime() - currentDate.getTime()
  const days = Math.ceil(diff / (1000 * 24 * 3600))

  let result = ''
  if (days > 0) {
    // Less than a week ago but more than a day ago
    if (days == 1) {
      result = i18n.global.t('dp.tommorrow')
    } else {
      result = `${days} ${
        days > 1 && lang.indexOf('en') > -1 ? i18n.global.t('dp.days') : i18n.global.t('dp.day')
      } ${i18n.global.t('dp.after')}`
    }
  } else if (days == 0) {
    // Less than a minute ago
    result = `${i18n.global.t('dp.today')}`
  } else {
    if (days == 1) {
      result = i18n.global.t('dp.yesterday')
    } else {
      result = `${Math.abs(days)} ${
        Math.abs(days) > 1 && lang.indexOf('en') > -1 ? i18n.global.t('dp.days') : i18n.global.t('dp.day')
      } ${i18n.global.t('dp.before')}`
    }
  }
  if (lang.includes('zh') && result !== '') {
    result = result.replace(/\s/g, '')
  }
  return result
}

export function calDayGap(timestamp: number): number {
  const incomingDate = new Date(timestamp)
  incomingDate.setHours(0, 0, 0, 0)
  const currentDate = new Date()
  currentDate.setHours(0, 0, 0, 0)
  const diff = incomingDate.getTime() - currentDate.getTime()
  const days = Math.ceil(diff / (1000 * 24 * 3600))
  return days
}

export function getSpecialAnniversaryLabel(num: number, lang: string, eventType: string, name: string): string {
  if (num <= 0 || num > 10000) {
    return name
  }

  const enSuffixes = ['th', 'st', 'nd', 'rd']
  const arSuffixes = ['', ' الأول', ' الثاني', ' الثالث']

  const getEnglishSuffix = (n: number) => {
    if (n % 100 >= 11 && n % 100 <= 13) {
      return enSuffixes[0]
    }
    switch (n % 10) {
      case 1:
        return enSuffixes[1]
      case 2:
        return enSuffixes[2]
      case 3:
        return enSuffixes[3]
      default:
        return enSuffixes[0]
    }
  }

  const getArabicSuffix = (n: number) => {
    switch (n) {
      case 1:
        return arSuffixes[1]
      case 2:
        return arSuffixes[2]
      case 3:
        return arSuffixes[3]
      default:
        return ' ' + n.toString()
    }
  }

  lang = lang.substring(0, 2)

  switch (lang) {
    case 'zh':
      return eventType === 'birthday'
        ? `${num}岁生日`
        : eventType === 'custom'
          ? `${num}周年 ` + name
          : `逝世${num}周年`
    case 'en':
      return eventType === 'birthday'
        ? `${num}${getEnglishSuffix(num)} birthday`
        : eventType === 'custom'
          ? `${num}${getEnglishSuffix(num)} ${name}`
          : `${num}${getEnglishSuffix(num)} remembrance`
    case 'fr':
      return eventType === 'birthday'
        ? `${num}ème anniversaire de naissance`
        : eventType === 'custom'
          ? `${num}ème jour de ${name}`
          : `${num}ème jour de souvenir`
    case 'es':
      return eventType === 'birthday'
        ? `${num}º cumpleaños`
        : eventType === 'custom'
          ? `${num}º día de ${name}`
          : `${num}º día de recuerdo`
    case 'ar':
      return eventType === 'birthday'
        ? `${num}${getArabicSuffix(num)} عيد ميلاد`
        : eventType === 'custom'
          ? `${num}${getArabicSuffix(num)} ${name}`
          : `${num}${getArabicSuffix(num)} إحياء الذكرى`
    default:
      throw new Error('Unsupported language. Supported languages are: zh, en, fr, es, ar.')
  }
}

export function formatDateToYYYYMMDD(date: Date): string {
  const year = date.getFullYear()
  let month = (date.getMonth() + 1).toString()
  let day = date.getDate().toString()

  // Add leading zeros to month and day if needed
  if (month.length < 2) {
    month = '0' + month
  }
  if (day.length < 2) {
    day = '0' + day
  }

  return `${year}-${month}-${day}`
}

export function formatYearsMonths(years: number, months: number, lang: string): string {
  let result = ''

  if (years > 0) {
    if (lang.startsWith('zh')) {
      result += years > 1 ? `${years}年` : `年`
    } else {
      result += years === 1 ? `yr` : `${years} yrs`
    }
  }

  if (months > 0) {
    if (result) {
      result += ' '
    }
    if (lang.startsWith('zh')) {
      result += `${months}月`
    } else {
      result += months === 1 ? `${months} mo` : `${months} mos`
    }
  }

  return result
}

export function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp)

  const year = date.getFullYear()

  const pad2 = (n: number) => (n < 10 ? '0' + n : '' + n)
  const month = pad2(date.getMonth() + 1)
  const day = pad2(date.getDate())

  return `${year}/${month}/${day}`
}

export function formatToYYMMDD(timestamp: number): string {
  const date = new Date(timestamp)
  const year = date.getFullYear().toString().slice(-2)
  const pad2 = (n: number) => (n < 10 ? '0' + n : '' + n)
  const month = pad2(date.getMonth() + 1)
  const day = pad2(date.getDate())
  return `${year}/${month}/${day}`
}

export function renderMoneyPeriod(amount: number, currency: string, years: number, months: number) {
  const prefix = currency === 'CNY' ? '￥' : '$'
  return prefix + amount / 100.0 + '/' + formatYearsMonths(years, months, currentLang.value) + ' '
}
export function renderMoney(amount: number, currency: string) {
  const prefix = currency === 'CNY' ? '￥' : '$'
  return prefix + amount / 100.0 + ' '
}

export function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds))
}

export function parseDateString(dateString: string): Date {
  let timestamp = Date.parse(dateString)
  let date = new Date(timestamp)
  return date
}

export function isAfterToday(targetDate: Date | null) {
  // Get today's date
  const today = new Date()

  // Set tomorrow's date by adding one day to today's date
  today.setHours(0, 0, 0, 0) // Set time to midnight

  // Compare the dates
  if (targetDate && targetDate > today) {
    return true
  } else {
    return false
  }
}

export function isNewer(baseDate: Date | null, targetDate: Date | null) {
  // Compare the dates
  if (targetDate && baseDate && targetDate > baseDate) {
    return true
  } else {
    return false
  }
}

export function toMonthAndDay(date: Date | null) {
  if (date) {
    const pad2 = (n: number) => (n < 10 ? '0' + n : '' + n)
    const month = pad2(date.getMonth() + 1)
    const day = pad2(date.getDate())
    return `${month}/${day}`
  }
  return '-'
}

const lunarMonths = [
  '',
  '正月',
  '二月',
  '三月',
  '四月',
  '五月',
  '六月',
  '七月',
  '八月',
  '九月',
  '十月',
  '十一月',
  '十二月'
]
const lunarDays = [
  '',
  '初一',
  '初二',
  '初三',
  '初四',
  '初五',
  '初六',
  '初七',
  '初八',
  '初九',
  '初十',
  '十一',
  '十二',
  '十三',
  '十四',
  '十五',
  '十六',
  '十七',
  '十八',
  '十九',
  '二十',
  '廿一',
  '廿二',
  '廿三',
  '廿四',
  '廿五',
  '廿六',
  '廿七',
  '廿八',
  '廿九',
  '三十'
]

/**
 * 将公历日期转换为农历日期并格式化
 * @param month 公历月份（1-12）
 * @param day 公历日期（1-31）
 * @returns 格式化的农历日期字符串
 */
export function convertToLunar(month: number, day: number): string {
  // 格式化农历日期
  const formattedLunarMonth = lunarMonths[month]
  const formattedLunarDay = lunarDays[day]

  return `${formattedLunarMonth}${formattedLunarDay}`
}

/**
 * 根据时间戳计算具体日期和距离今天的天数
 * @param timestamp 时间戳（毫秒）
 * @returns { dateString: string, daysFromNow: number } 日期字符串（x月x日）和距离今天的天数（向上取整）
 */
export function computedLeftDate(
  timestamp: number,
  lang: string = 'zh-CN'
): { dateString: string; daysFromNow: string } {
  lang = lang.replace('_', '-')

  const targetDate = new Date(timestamp)
  const today = new Date()

  // 计算精确的时间差（毫秒）
  const timeDiff = targetDate.getTime() - today.getTime()

  // 计算天、小时、分钟
  const MS_PER_MINUTE = 1000 * 60
  const MS_PER_HOUR = MS_PER_MINUTE * 60
  const MS_PER_DAY = MS_PER_HOUR * 24

  let daysFromNow = ''

  if (timeDiff >= MS_PER_DAY) {
    // 大于一日，返回"xx天"
    const days = Math.floor(timeDiff / MS_PER_DAY)
    if (lang.indexOf('zh') > -1) {
      daysFromNow = `${days}天`
    } else {
      daysFromNow = `${days} ${days > 1 ? i18n.global.t('dp.days') : i18n.global.t('dp.day')}`
    }
  } else if (timeDiff >= MS_PER_HOUR) {
    // 小于一日，大于一小时，返回"xx.x小时"（保留一位小数）
    const hours = (timeDiff / MS_PER_HOUR).toFixed(1)
    if (lang.indexOf('zh') > -1) {
      daysFromNow = `${hours}小时`
    } else {
      daysFromNow = `${hours} ${parseFloat(hours) > 1 ? i18n.global.t('dp.hours') : i18n.global.t('dp.hour')}`
    }
  } else if (timeDiff >= MS_PER_MINUTE) {
    // 大于一分钟，小于一小时，返回"xx分钟"
    const minutes = Math.floor(timeDiff / MS_PER_MINUTE)
    if (lang.indexOf('zh') > -1) {
      daysFromNow = `${minutes}分钟`
    } else {
      daysFromNow = `${minutes} ${minutes > 1 ? i18n.global.t('dp.minutes') : i18n.global.t('dp.minute')}`
    }
  } else {
    // 不足一分钟，返回"0分钟"
    if (lang.indexOf('zh') > -1) {
      daysFromNow = '0分钟'
    } else {
      daysFromNow = `0 ${i18n.global.t('dp.minute')}`
    }
  }

  // 格式化日期
  let dateString = ''
  if (lang.indexOf('zh') > -1) {
    // 中文格式：x月x日
    const month = targetDate.getMonth() + 1 // getMonth() 返回 0-11，所以要加1
    const day = targetDate.getDate()
    dateString = `${month}月${day}日`
  } else {
    // 英文格式：使用缩写 Feb 3
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric'
    }
    dateString = targetDate.toLocaleDateString(lang, options)
  }

  return {
    dateString,
    daysFromNow
  }
}
